import { useGetAllArtworksCoreInfo } from "@/api/artwork/artwork";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HomeDialogHeader } from "../HomeDialogHeader";
import { DatabaseIcon } from "../HomeIcons";
import { HomeSearchInput } from "../HomeSearchInput";

const PAGE = { page: 0, pageSize: 20 };

/**
 * Retrouve une œuvre par son titre, son numéro d'enregistrement ou sa date de
 * création (la recherche de l'API porte sur ces trois champs) et ouvre sa
 * fiche, d'où elle s'exporte.
 */
export const FindArtworkDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const vt = useVisibleT();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");

  // Une requête par pause de frappe, pas une par lettre.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching } = useGetAllArtworksCoreInfo(
    PAGE,
    search || undefined,
  );
  const artworks = data?.data ?? [];

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setQuery("");
      setSearch("");
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        needCloseButton={false}
        className="flex max-h-[90vh] max-w-2xl flex-col gap-0 rounded-xl p-6 text-slate-900 sm:rounded-xl"
      >
        <HomeDialogHeader
          icon={<DatabaseIcon />}
          title={vt("bo.home.findArtwork.title", "Exporter une œuvre")}
          description={vt(
            "bo.home.findArtwork.subtitle",
            "Retrouvez l’œuvre, puis exportez-la depuis sa fiche.",
          )}
        />

        <HomeSearchInput
          value={query}
          onChange={setQuery}
          placeholder={vt(
            "bo.home.findArtwork.placeholder",
            "Titre, numéro d’enregistrement ou date de création",
          )}
          autoFocus
          className="mt-4"
        />

        <div className="-mx-1 mt-4 min-h-[120px] flex-1 overflow-y-auto px-1">
          {isFetching && artworks.length === 0 ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 rounded-md" />
              ))}
            </div>
          ) : artworks.length === 0 ? (
            <p className="text-sm text-slate-500">
              {vt("bo.home.findArtwork.empty", "Aucune œuvre trouvée.")}
            </p>
          ) : (
            <ul className="flex list-none flex-col gap-1.5">
              {artworks.map((artwork) => (
                <li key={artwork.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/edit/artwork/${artwork.id}`)}
                    className="flex w-full min-w-0 items-center gap-3 rounded-lg border border-slate-200 p-2 text-left transition-colors hover:border-primary hover:bg-brand-50"
                  >
                    {artwork.url ? (
                      <img
                        src={artwork.url}
                        alt=""
                        className="size-10 shrink-0 rounded-md bg-slate-100 object-cover"
                      />
                    ) : (
                      <span className="size-10 shrink-0 rounded-md bg-slate-100" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {artwork.oeuvresDescriptionTitre ||
                          vt("bo.home.pin.untitled", "Sans titre")}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {[
                          artwork.oeuvresDescriptionNumeroEnregistrement,
                          artwork.oeuvresDescriptionDateDeCreation,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
